import {
  Card,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import "../../App.css";
import IosShareIcon from "@mui/icons-material/IosShare";
import {
  StyledCardTitle,
  StyledExport,
} from "../../StyledComponents/StyledComponents";
import { Search, ContentCopy } from "@mui/icons-material";
import StickyHeadTable from "../../components/table";
import SelectLabels from "../../components/menuItem";
import { AppContext } from "../../Context/AppContext";
import { useNavigate } from "react-router-dom";
import {
  planDetailsApi,
  csvReadApi,
  getAllVersionsApi,
  postVersionApi,
  updateVersionApi,
  deleteVersionApi,
} from "../../api/adminApi";
import DateRangeSelector from "../../components/datePicker";
import { Helmet } from "react-helmet-async";
import { debounce } from "lodash";
import { isEmailAllowedForMembers } from "../../utils/auth";

export default function PlanDetails() {
  const { limit } = useContext(AppContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
  const drawerWidth = 0;
  const [searchText, setSearchText] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const [Loader, setLoader] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(0);
  const [planData, setData] = useState([]);
  const [planCount, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const noDataFound  = planData.length === 0;

  // Developer operations state
  const [isCsvOpen, setIsCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvResults, setCsvResults] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);

  const [isVersionOpen, setIsVersionOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [showVersionForm, setShowVersionForm] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);

  // Version form fields
  const [verAppName, setVerAppName] = useState("DUCO");
  const [verLatest, setVerLatest] = useState("");
  const [verBelow, setVerBelow] = useState("");
  const [verImage, setVerImage] = useState("");

  const serializedObject = sessionStorage?.getItem("userDetails");
  const userDetails = serializedObject ? JSON.parse(serializedObject) : null;
  const adminEmail = userDetails?.email || "";
  const isDeveloper = isEmailAllowedForMembers(adminEmail);

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    if (csvFile.type !== "text/csv") {
      alert("Please upload a valid CSV file.");
      return;
    }
    setCsvUploading(true);
    setCsvResults(null);
    try {
      const res = await csvReadApi(csvFile);
      if (res && res.Data) {
        setCsvResults(res.Data);
      } else {
        alert("Upload failed: " + (res?.message || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      alert("Error uploading file: " + err.message);
    } finally {
      setCsvUploading(false);
    }
  };

  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!csvResults || csvResults.length === 0) return;
    const textToCopy = csvResults.join("\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOpenVersions = async () => {
    setIsVersionOpen(true);
    await loadVersions();
  };

  const loadVersions = async () => {
    setVersionLoading(true);
    try {
      const res = await getAllVersionsApi();
      if (Array.isArray(res)) {
        setVersions(res);
      } else {
        setVersions([]);
      }
    } catch (err) {
      console.error(err);
      setVersions([]);
    }
    setVersionLoading(false);
  };

  const handleDeleteVersion = async (appName) => {
    if (!window.confirm(`Are you sure you want to delete the version data for ${appName}?`)) return;
    try {
      const res = await deleteVersionApi(appName);
      if (res && res.success) {
        alert("Version deleted successfully!");
        await loadVersions();
      } else {
        alert("Delete failed: " + (res?.message || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditVersion = (ver) => {
    setEditingVersion(ver);
    setVerAppName(ver.AppName);
    setVerLatest(ver.LatestVersion);
    setVerBelow(ver.BelowVersion);
    setVerImage(ver.Image || "");
    setShowVersionForm(true);
  };

  const handleCreateOrUpdateVersion = async (e) => {
    e.preventDefault();
    if (!verLatest || !verBelow) {
      alert("Latest Version and Below Version are required.");
      return;
    }
    const versionRegex = /^(\d+\.)?(\d+\.)?(\*|\d+)$/;
    if (!versionRegex.test(verLatest) || !versionRegex.test(verBelow)) {
      alert("Invalid version format. Please use a format like 1.0.5");
      return;
    }
    const payload = {
      AppName: verAppName,
      LatestVersion: verLatest,
      BelowVersion: verBelow,
      MinimumSupportedVersion: verBelow,
      Image: verImage
    };
    try {
      let res;
      if (editingVersion) {
        res = await updateVersionApi(payload);
      } else {
        res = await postVersionApi(payload);
      }
      if (res && res.success) {
        alert(editingVersion ? "Version updated successfully!" : "Version added successfully!");
        setShowVersionForm(false);
        setEditingVersion(null);
        setVerLatest("");
        setVerBelow("");
        setVerImage("");
        await loadVersions();
      } else {
        alert("Operation failed: " + (res?.message || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      alert("Error: " + err.message);
    }
  };

  const handleAddNewVersionClick = () => {
    setEditingVersion(null);
    setVerAppName("DUCO");
    setVerLatest("");
    setVerBelow("");
    setVerImage("");
    setShowVersionForm(true);
  };
  
  const resultLimit = limit === "" ? 10 : limit;
  function formatDate(inputDateStr) {
    if (!inputDateStr) return "";
    const dateVal = inputDateStr.$d || inputDateStr;
    const dateObj = new Date(dateVal);
    if (!isNaN(dateObj.getTime())) {
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const year = String(dateObj.getFullYear());
      return `${month}-${day}-${year}`;
    }
    return "";
  }
  async function handleDownload() {
    setLoading(true);

    let isForSheet = "true";
    planDetailsApi(page * resultLimit, resultLimit, searchText, isForSheet).then(() => {
      setLoading(false);
    });
  }
  const debouncedAPICall = debounce(async (query) => {
    let isForSheet = "false";
    setIsLoading(true);
    await planDetailsApi(
      0,
      resultLimit,
      query,
      isForSheet,
      formatDate(startDate),
      formatDate(endDate)
    ).then((res) => {
      if (res?.statusCode == 404) {
        navigate("/");
      }
      setIsLoading(false);
      setData(Array.isArray(res?.message) ? res.message : []);
      console.log('Fetched planData count:', Array.isArray(res?.message) ? res.message.length : 0);
      console.log('First item:', Array.isArray(res?.message) && res.message.length > 0 ? res.message[0] : null);
      setPage(0);
      setCount(Number(res?.count) || 0);
    });
  }, 0);
  const handleSearchChange = async (event) => {
    let { value } = event.target;
    if (value == "") {
      await debouncedAPICall(value);
    }
    setSearchText(value);
  };
  const handlePageChange = (params) => {
    setPage(params.page);
  };
  const handleStartDateChange = (date) => {
    setStartDate(date);
    setPage(0);
  };
  const handleSearchClick = async () => {
    await debouncedAPICall(searchText);
    setLoader(false);
  };
  const handleEndDateChange = (date) => {
    setEndDate(date);
    setPage(0);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    await debouncedAPICall(searchText);
    setIsSubmit(false);
  };

  useEffect(() => {
    let isForSheet = "false";
    setIsLoading(true);

    planDetailsApi(
      page * resultLimit,
      resultLimit,
      searchText,
      isForSheet,
      formatDate(startDate),
      formatDate(endDate)
    ).then((res) => {
      if (res?.statusCode == 404) {
        navigate("/");
      }
      setData(Array.isArray(res?.message) ? res.message : []);
      setCount(Number(res?.count) || 0);
      setLoader(false);
      setIsLoading(false);
    });
  }, [page, limit, startDate, endDate]);

  if (Loader == true) {
    return (
      <div className="ayoub">
        <div className="loader"></div>
      </div>
    );
  } else {
    return (
      <>
        <Helmet>
          <title> Plan Details </title>
        </Helmet>
        <Grid
          container
          sx={{
            p: "25px",
          }}
        >
          <Grid
            item
            sx={{
              backgroundColor: "#FAF2FF",
              width: isSmallerScreen ? "100%" : `calc(100% - ${drawerWidth}px)`,
              ml: { md: `${drawerWidth}px` },
            }}
          >
            <Grid container direction={"column"}>
              <Grid item>
                <Card>
                  <CardHeader
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    title={
                      <Grid
                        display="flex"
                        alignItems={"center"}
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                      >
                        <StyledCardTitle> Plan Details </StyledCardTitle>
                        
                        <Box sx={{ display: "flex", gap: 2 }}>
                          {isDeveloper && (
                            <>
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setCsvFile(null);
                                  setCsvResults(null);
                                  setIsCsvOpen(true);
                                }}
                                sx={{ bgcolor: "#6A5AE0", color: "white", borderRadius: "8px", textTransform: "none", fontFamily: "Open Sans", '&:hover': { bgcolor: "#5748c9" } }}
                              >
                                Upload Failed Payments CSV
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleOpenVersions}
                                sx={{ bgcolor: "#6A5AE0", color: "white", borderRadius: "8px", textTransform: "none", fontFamily: "Open Sans", '&:hover': { bgcolor: "#5748c9" } }}
                              >
                                Manage App Versions
                              </Button>
                            </>
                          )}
                          <StyledExport
                            variant="contained"
                            onClick={handleDownload}
                            disabled={noDataFound}
                          >
                            {loading ? (
                              <CircularProgress
                                size={25}
                                sx={{
                                  color: "white", 
                                }}
                              />
                            ) : (
                              <IosShareIcon
                                sx={{
                                  width: "19px",
                                  height: "19px",
                                  pb: "5px",
                                }}
                              />
                            )}
                            Export
                          </StyledExport>
                        </Box>
                      </Grid>
                    }
                  />
                  <Divider />
                  <Card sx={{ height: "100%" }}>
                    
                              
                    <Grid
                      container
                      direction="row"
                      alignItems={"start"}
                      justifyContent={"space-evenly"}
                    >
                      <Grid item xl={1.8} lg={2.8} md={3.8} sm={5.9} xs={5.9}>
                        <CardHeader
                          sx={{ p: "12px" }}
                          title={
                            <Grid
                              container
                              direction={"row"}
                              columnSpacing={3}
                              justifyContent={"space-between"}
                              alignItems={"center"}
                            >
                              <Grid item xl={4.5} lg={4} md={4} sm={4} xs={5}>
                                <Typography
                                  sx={{
                                    color: "#A6A4A4",
                                  }}
                                >
                                  Results:
                                </Typography>
                              </Grid>
                              <Grid item xl={7.5} lg={8} md={8} sm={8} xs={7}>
                                <SelectLabels />
                              </Grid>
                            </Grid>
                          }
                        />
                      </Grid>
                      {/* {isSmallerScreen ? null : ( */}
                      <Grid
                        item
                        xl={0.01}
                        lg={0.01}
                        md={0.01}
                        sm={0.01}
                        xs={0.01}
                      >
                        <Divider
                          sx={{
                            height: "80px",
                          }}
                          orientation="vertical"
                        />
                      </Grid>
                      {/* )} */}
                      {isSmallerScreen ? null : (
                        <Grid item xl={6} lg={5} md={4} xs={4}>
                          <CardHeader
                            sx={{ p: "12px" }}
                            title={
                              <form onSubmit={handleSearchSubmit}>
                                <TextField
                                  sx={{ mr: 1 }}
                                  id="outlined-basic"
                                  className="search-input"
                                  placeholder="Search..."
                                  autoComplete="off"
                                  value={searchText}
                                  onChange={handleSearchChange}
                                  inputProps={{ maxLength: 100 }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton onClick={handleSearchClick}>
                                          <Search />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </form>
                            }
                          />
                        </Grid>
                      )}
                      {isSmallerScreen ? null : (
                        <Grid
                          item
                          xl={0.01}
                          lg={0.01}
                          md={0.01}
                          sm={0.01}
                          xs={0.01}
                        >
                          <Divider
                            sx={{
                              height: "80px",
                            }}
                            orientation="vertical"
                          />
                        </Grid>
                      )}
                      {/* {isSmallerScreen ? null : ( */}
                      <Grid item xl={4} lg={4} md={4} sm={6} xs={6}>
                        <CardHeader
                          sx={{ p: "12px" }}
                          title={
                            <DateRangeSelector
                              endDate={endDate}
                              setEndDate={setEndDate}
                              setStartDate={setStartDate}
                              startDate={startDate}
                              handleStartDateChange={handleStartDateChange}
                              handleEndDateChange={handleEndDateChange}
                            />
                          }
                        />
                      </Grid>
                      {/* )} */}
                    </Grid>
                  </Card>
                </Card>
              </Grid>
              <StickyHeadTable
                handlePageChange={handlePageChange}
                planData={planData}
                planCount={Number(planCount) || 0}
                page={page}
                resultLimit={resultLimit}
                setPage={setPage}
                Loader={Loader}
                isLoading={isLoading}
              />
            </Grid>
          </Grid>
        </Grid>

        <Dialog open={isCsvOpen} onClose={() => setIsCsvOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ color: "#6A5AE0", fontWeight: "bold" }}>Upload Failed Payments CSV</DialogTitle>
          <DialogContent dividers>
            <Box component="form" onSubmit={handleCsvUpload} sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
              <Typography variant="body2" sx={{ color: "#8E8D9A", fontFamily: "Open Sans" }}>
                Select a CSV file containing payment ID records. The system will identify IDs that do not exist in the database.
              </Typography>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                style={{ margin: "10px 0" }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={csvUploading || !csvFile}
                sx={{ alignSelf: "flex-start", bgcolor: "#6A5AE0", color: "white", borderRadius: "8px", '&:hover': { bgcolor: "#5748c9" } }}
              >
                {csvUploading ? <CircularProgress size={20} color="inherit" /> : "Upload and Read"}
              </Button>
            </Box>

            {csvResults && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#2B2A35", fontFamily: "Open Sans" }}>
                    Failed Payment IDs ({csvResults.length}):
                  </Typography>
                  {csvResults.length > 0 && (
                    <Button
                      size="small"
                      startIcon={<ContentCopy fontSize="small" />}
                      onClick={handleCopy}
                      sx={{ textTransform: "none", color: "#6A5AE0", fontWeight: "bold", fontFamily: "Open Sans" }}
                    >
                      {copied ? "Copied!" : "Copy All"}
                    </Button>
                  )}
                </Box>
                {csvResults.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "green", fontFamily: "Open Sans" }}>
                    All payments in the CSV exist in the database! No failures found.
                  </Typography>
                ) : (
                  <Paper sx={{ p: 2, maxHeight: "200px", overflowY: "auto", bgcolor: "#FCF8FF" }}>
                    {csvResults.map((id, index) => (
                      <Typography key={index} variant="body2" sx={{ fontFamily: "monospace", color: "#6A5AE0" }}>
                        {id}
                      </Typography>
                    ))}
                  </Paper>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsCsvOpen(false)} sx={{ fontFamily: "Open Sans" }}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isVersionOpen} onClose={() => setIsVersionOpen(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ color: "#6A5AE0", fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "Open Sans" }}>
            <span>App Version Management</span>
            {!showVersionForm && (
              <Button
                variant="contained"
                onClick={handleAddNewVersionClick}
                sx={{ bgcolor: "#6A5AE0", color: "white", borderRadius: "8px", '&:hover': { bgcolor: "#5748c9" } }}
              >
                Add New Version
              </Button>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {versionLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress sx={{ color: "#6A5AE0" }} />
              </Box>
            ) : showVersionForm ? (
              <Box component="form" onSubmit={handleCreateOrUpdateVersion} sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#2B2A35", fontFamily: "Open Sans" }}>
                  {editingVersion ? "Edit Version" : "Create New App Version"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="ver-app-label">App Name<span style={{ color: "red" }}>*</span></InputLabel>
                      <Select
                        labelId="ver-app-label"
                        value={verAppName}
                        label={<span>App Name<span style={{ color: "red" }}>*</span></span>}
                        disabled={!!editingVersion}
                        onChange={(e) => setVerAppName(e.target.value)}
                      >
                        <MenuItem value="DUCO">DUCO</MenuItem>
                        <MenuItem value="UII">UII</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Latest Version"
                      placeholder="e.g. 1.0.5"
                      size="small"
                      fullWidth
                      required
                      value={verLatest}
                      onChange={(e) => setVerLatest(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Below Version (Force Update threshold)"
                      placeholder="e.g. 1.0.0"
                      size="small"
                      fullWidth
                      required
                      value={verBelow}
                      onChange={(e) => setVerBelow(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Store Image URL (Optional)"
                      placeholder="URL string"
                      size="small"
                      fullWidth
                      value={verImage}
                      onChange={(e) => setVerImage(e.target.value)}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ bgcolor: "#6A5AE0", color: "white", borderRadius: "8px", '&:hover': { bgcolor: "#5748c9" } }}
                  >
                    {editingVersion ? "Update" : "Create"}
                  </Button>
                  <Button variant="outlined" onClick={() => setShowVersionForm(false)} sx={{ borderRadius: "8px" }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#FCF8FF" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", fontFamily: "Open Sans" }}>App Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontFamily: "Open Sans" }}>Latest Version</TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontFamily: "Open Sans" }}>Below Version</TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontFamily: "Open Sans" }}>Play Store URL</TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontFamily: "Open Sans" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: "#8E8D9A", py: 4, fontFamily: "Open Sans" }}>
                          No app version settings found in DB.
                        </TableCell>
                      </TableRow>
                    ) : (
                      versions.map((ver) => (
                        <TableRow key={ver._id}>
                          <TableCell sx={{ fontWeight: "bold", color: "#6A5AE0", fontFamily: "Open Sans" }}>{ver.AppName}</TableCell>
                          <TableCell sx={{ fontFamily: "Open Sans" }}>{ver.LatestVersion}</TableCell>
                          <TableCell sx={{ fontFamily: "Open Sans" }}>{ver.BelowVersion}</TableCell>
                          <TableCell sx={{ fontSize: "11px", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "Open Sans" }}>
                            {ver.PlayStoreUrl || "-"}
                          </TableCell>
                          <TableCell>
                            <Button size="small" onClick={() => handleEditVersion(ver)} sx={{ mr: 1, textTransform: "none", fontFamily: "Open Sans" }}>
                              Edit
                            </Button>
                            <Button size="small" color="error" onClick={() => handleDeleteVersion(ver.AppName)} sx={{ textTransform: "none", fontFamily: "Open Sans" }}>
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsVersionOpen(false)} sx={{ fontFamily: "Open Sans" }}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}
